import { type KnownUser } from '@databases/types/confluence';

import {
  type AsyncOperation,
  AsyncOperationStatus,
  validateStatus
} from '../async-operation.ts';

import { type UsersState } from './slice.ts';

type ContextState = {
  users: UsersState;
};

/**
 * Returns the user with the specified account ID from the store. If the user does not exist or an error occurred
 * while loading it, an error is thrown.
 * @throws If the user does not exist or an error occurred while loading it, an error is thrown.
 */
export function selectUserByIdRequired(
  state: ContextState,
  { accountId }: { accountId: string }
): KnownUser {
  validateStatus(selectUserByIdStatus(state, { accountId }));

  const user = selectUserByIdOptional(state, { accountId });
  if (!user) {
    throw new Error(`Could not find user ${accountId}.`);
  }
  return user;
}

/**
 * Returns the user with the specified account ID from the store. If the user does not exist, undefined is returned.
 * If there was an error loading the user, undefined is returned.
 */
export function selectUserByIdOptional(
  state: ContextState,
  { accountId }: { accountId: string | undefined }
): KnownUser | undefined {
  if (!accountId) {
    return undefined;
  }

  if (selectUserByIdStatus(state, { accountId })?.status === AsyncOperationStatus.ERROR) {
    return undefined;
  }

  return state.users.users[accountId];
}

export function selectUsersByIds(
  state: ContextState,
  { accountIds }: { accountIds: string[] }
): Record<string, KnownUser | undefined> {
  return Object.fromEntries(
    accountIds.map((accountId) => [
      accountId,
      selectUserByIdOptional(state, { accountId })
    ])
  );
}

/**
 * Returns the load status of the loadUserById action for the given account ID.
 */
export function selectUserByIdStatus(
  state: ContextState,
  { accountId }: { accountId: string | undefined }
): AsyncOperation<void> | undefined {
  if (!accountId) {
    return undefined;
  }

  return state.users.userByIdStatus[accountId];
}

export function selectUsersByIdsStatus(
  state: ContextState,
  { accountIds }: { accountIds: string[] }
): Record<string, AsyncOperation<void> | undefined> {
  return Object.fromEntries(
    accountIds.map((accountId) => [accountId, selectUserByIdStatus(state, { accountId })])
  );
}
